import {
  createContext,
  useContext,
  Accessor,
  createSignal,
  ParentProps,
  JSX,
  createComputed,
  createEffect,
  createMemo,
  onCleanup,
  children,
} from 'solid-js';
import { resolveElements, resolveFirst } from '@solid-primitives/refs';
import { createVisibilityObserver } from '@solid-primitives/intersection-observer';

const Ctx = createContext<Accessor<boolean>>(() => true);

/**
 * 表示应用当前是否在激活，可以对离屏的应用做性能优化。
 * 当设置了value就使用value的值
 * 当没有设置value就使用intersection-observer自动检测元素是否可见, 只有第一个dom生效
 * @param props
 * @returns
 */
export function ActiveProvider(
  props: ParentProps<{
    value?: boolean;
    /** 强制设置，忽略上下文状态 */
    force?: boolean;
  }>
) {
  const parentActive = useIsActive();
  function mergeActive(value: Accessor<boolean>) {
    return createMemo(() => {
      if (props.force) {
        return value();
      } else {
        return parentActive() && value();
      }
    });
  }

  if (props.value !== undefined) {
    return (
      <Ctx.Provider value={mergeActive(() => props.value!)}>
        {props.children}
      </Ctx.Provider>
    );
  } else {
    const [value, setValue] = createSignal(false);
    const childs = children(() => (
      <Ctx.Provider value={mergeActive(value)}>{props.children}</Ctx.Provider>
    ));
    const first = resolveFirst(childs);
    const active = createVisibilityObserver()(first);
    createComputed(() => setValue(active()));
    return childs as unknown as JSX.Element;
  }
}

export function useIsActive() {
  return useContext(Ctx);
}

/** 只在当前UI可用时进行更新，否则不进行更新，实现在后台挂起的时候暂停应用运行 */
export function useActiveMemo<T>(value: Accessor<T>): Accessor<T> {
  const active = useIsActive();
  let cache: T | undefined;
  return () => {
    if (!cache || active()) {
      cache = value();
    }
    return cache;
  };
}

/** 包装一个signal，仅在UI激活时进行更新, 感觉没必要，增加了额外api */
// export function useActiveSignal<T> (signal: Signal<T>) {
//   return [useActiveMemo(signal[0]) ,signal[1]] as const;
// }

/**
 * 恢复滚动条信息, 用于缓存dom的时候
 * 一般有两种情况会工作，当上下文active状态为false的时候，或者children为空的时候
 * @param props
 * @returns
 */
export function KeepScroll(props: ParentProps) {
  const scroll = {
    scrollTop: 0,
    scrollLeft: 0,
  };
  const active = useIsActive();
  const children = resolveElements(() => props.children);
  const firstChild = createMemo(() => {
    const els = children();
    return active() ? (els instanceof Array ? els[0] : els) : null;
  });
  createComputed(() => {
    const el = firstChild();
    if (el) {
      onCleanup(() => {
        scroll.scrollTop = el.scrollTop;
        scroll.scrollLeft = el.scrollLeft;
      });
    }
  });
  createEffect(() => {
    const el = firstChild();
    if (el) {
      el.scrollTop = scroll.scrollTop;
      el.scrollLeft = scroll.scrollLeft;
    }
  });
  return children as unknown as JSX.Element;
}
